import { useState, useEffect, useCallback } from "react";
import {
    CurrencyCode,
    CURRENCIES,
    DEFAULT_CURRENCY,
    CURRENCY_STORAGE_KEY,
    getCurrencyInfo,
    isValidCurrency,
} from "@/models/currency";

export interface UseCurrencyReturn {
    currency: CurrencyCode;
    updateCurrency: (newCurrency: CurrencyCode) => void;
    currencies: typeof CURRENCIES;
    currentCurrencyInfo: ReturnType<typeof getCurrencyInfo>;
    isLoading: boolean;
}

/**
 * Hook per gestire la valuta selezionata dall'utente
 * - Persiste in localStorage
 * - Al primo accesso usa sempre EUR come default
 * - Quando cambia valuta, ricarica la pagina per applicare la nuova valuta
 * - Il backend convertirà i prezzi in base all'header X-Currency
 */
export const useCurrency = (): UseCurrencyReturn => {
    const [currency, setCurrency] = useState<CurrencyCode>(DEFAULT_CURRENCY);
    const [isLoading, setIsLoading] = useState(true);

    // Al mount: carica currency da localStorage o auto-detect
    useEffect(() => {
        if (typeof window === "undefined") return;

        setIsLoading(true);

        try {
            // Prova a leggere da localStorage
            const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);

            if (savedCurrency && isValidCurrency(savedCurrency)) {
                // Usa la valuta salvata
                setCurrency(savedCurrency);
            } else {
                // Al primo accesso, usa sempre USD come default
                setCurrency(DEFAULT_CURRENCY);
                localStorage.setItem(CURRENCY_STORAGE_KEY, DEFAULT_CURRENCY);
            }
        } catch (error) {
            console.error("Error loading currency from localStorage:", error);
            setCurrency(DEFAULT_CURRENCY);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Funzione per aggiornare la valuta
    const updateCurrency = useCallback(
        (newCurrency: CurrencyCode) => {
            if (!isValidCurrency(newCurrency)) {
                console.error(`Invalid currency code: ${newCurrency}`);
                return;
            }

            // Se la valuta non è cambiata, non fare nulla
            if (newCurrency === currency) {
                return;
            }

            try {
                // Salva in localStorage
                localStorage.setItem(CURRENCY_STORAGE_KEY, newCurrency);

                // Ricarica la pagina per applicare la nuova valuta
                // Il backend convertirà i prezzi in base all'header X-Currency
                window.location.reload();
            } catch (error) {
                console.error("Error updating currency:", error);
            }
        },
        [currency]
    );

    return {
        currency,
        updateCurrency,
        currencies: CURRENCIES,
        currentCurrencyInfo: getCurrencyInfo(currency),
        isLoading,
    };
};
